/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

const createEvent = (event) => {
    const { type, value } = event, other = __rest(event, ["type", "value"]);
    return Object.assign(Object.assign({ type, timestamp: Date.now() }, value ? { value } : {}), other);
};
function transitionToEvent(url) {
    return createEvent({
        type: 'transition',
        destination: url
    });
}

const setAttribute = (key, value) => {
    return {
        [key]: value
    };
};

const sendToServer = (request) => __awaiter(void 0, void 0, void 0, function* () {
    const { session_id, config, data, mode } = request;
    const payload = {
        session_id: session_id,
        attributes: data.attributes,
        events: data.events
    };
    const endpoint = `${config.endpoint}/${config.client_secret}`;
    try {
        if (mode === 'beacon') {
            navigator.sendBeacon(endpoint, JSON.stringify(payload));
            return;
        }
        const response = yield fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error(`Failed to send data to server: ${response.status} ${response.statusText}`);
        }
        console.log('status: ', response.status, 'statusText: ', response.statusText);
        return;
    }
    catch (error) {
        console.error("Failed to send analytics data:", error);
    }
});

const UUID_V4_FORMAT = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
// @see: https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid
function uuid() {
    let d = new Date().getTime();
    let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;
    return UUID_V4_FORMAT.replace(/[xy]/g, function (c) {
        let r = Math.random() * 16;
        if (d > 0) {
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        }
        else {
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
}

/**
 * Batching handler for attributes and events.
 * Manages the collection and dispatch of data based on the provided batching configuration.
 */
class BatchingHandler {
    constructor(config) {
        this.sendMode = 'fetch';
        this.session_id = '';
        this.attributes = [];
        this.events = [];
        this.batchCounter = 0;
        this.config = config;
        this.startSession();
    }
    /**
     * Starts a new session.
     */
    startSession() {
        if (this.config.uuidProvider === undefined) {
            this.session_id = uuid();
            return;
        }
        this.session_id = this.config.uuidProvider();
    }
    /**
     * Add an attribute to the batch.
     *
     * @param attribute - The attribute to be added to the batch.
     */
    addAttribute(attribute) {
        this.attributes.push(attribute);
        this.checkBatchThreshold();
    }
    /**
     * Add an event to the batch.
     *
     * @param event - The event to be added to the batch.
     */
    addEvent(event) {
        this.events.push(event);
        this.checkBatchThreshold();
    }
    /**
     * Checks if the current batch meets the threshold for sending to the server.
     */
    checkBatchThreshold() {
        this.batchCounter++;
        if (this.shouldSendBatch()) {
            this.sendBatch().then(() => {
                // do nothing
            });
        }
    }
    /**
     * Determines whether the current batch should be sent to the server.
     *
     * @returns {boolean} - True if the batch should be sent, false otherwise.
     */
    shouldSendBatch() {
        return this.config.batching !== undefined && this.config.batching > 0 && this.batchCounter >= this.config.batching;
    }
    /**
     * Sends the current batch to the server and resets the batch.
     */
    sendBatch() {
        return __awaiter(this, void 0, void 0, function* () {
            const attributes = [...this.attributes];
            const events = [...this.events];
            return new Promise((resolve, reject) => {
                console.log('preparing to send batch attribute length = ', attributes.length, 'event length = ', events.length);
                sendToServer({
                    session_id: this.session_id,
                    config: this.config,
                    data: {
                        attributes: attributes.reduce((acc, attribute) => {
                            return Object.assign(Object.assign({}, acc), attribute);
                        }, {}),
                        events,
                    },
                    mode: this.sendMode,
                }).then((r) => {
                    resolve();
                }).catch((error) => {
                    console.warn("Failed to send analytics data:", error);
                    reject(error);
                });
                this.attributes = [];
                this.events = [];
                this.batchCounter = 0;
            });
        });
    }
    /**
     * Force sends the current batch irrespective of the batch size.
     * Useful for end-of-session scenarios.
     */
    forceSend() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.attributes.length > 0 || this.events.length > 0) {
                return yield this.sendBatch();
            }
        });
    }
    /**
     * Switches to beacon mode.
     */
    useBeacon() {
        this.sendMode = 'beacon';
    }
    /**
     * Switches to fetch mode.
     */
    useFetch() {
        this.sendMode = 'fetch';
    }
    setSessionId(sessionId) {
        this.session_id = sessionId;
    }
    getEvents() {
        return this.events;
    }
    getAttributes() {
        return this.attributes;
    }
}

function containsUuid(text) {
    const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
    return uuidRegex.test(text);
}

const createAnalytics = (_a) => {
    var { batching = 50 } = _a, other = __rest(_a, ["batching"]);
    const handler = new BatchingHandler(Object.assign({ batching }, other));
    return {
        getHandler: () => {
            return handler;
        },
        getEvents: () => {
            return handler.getEvents();
        },
        getAttributes: () => {
            return handler.getAttributes();
        },
        setAttribute: (key, value) => {
            const attribute = setAttribute(key, value);
            handler.addAttribute(attribute);
        },
        setAttributes: (attributes) => {
            Object.entries(attributes).forEach(([key, value]) => {
                const attribute = setAttribute(key, value);
                handler.addAttribute(attribute);
            });
        },
        startSpecificSession: (session_id) => {
            if (!containsUuid(session_id)) {
                console.error('Invalid session id provided, must contain a uuid.');
                return;
            }
            handler.setSessionId(session_id);
            handler.addEvent(createEvent({
                type: 'session_start',
            }));
        },
        startWithoutSessionEvent: () => {
            handler.startSession();
        },
        startSession: () => {
            handler.startSession();
            handler.addEvent(createEvent({
                type: 'session_start',
            }));
        },
        setEvent: (event) => {
            handler.addEvent(createEvent(event));
        },
        transitionTo: (url) => {
            const event = transitionToEvent(url);
            handler.addEvent(event);
        },
        endSession: () => {
            handler.addEvent(createEvent({
                type: 'session_end',
            }));
            return handler.forceSend();
        },
        forceSend: () => {
            return handler.forceSend();
        },
        handleUnload: () => {
            // add event handler
            // switch to beacon mode
            // send data
            window.addEventListener('unload', () => {
                handler.useBeacon();
                handler.forceSend().then(() => {
                    // do nothing
                });
            });
        }
    };
};

export { createAnalytics };
